import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

// Services
import AuthService from "services/auth-service";

function ActivateAccount() {
  const { userId, activationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activationSuccess, setActivationSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const activateAccount = async () => {
      try {
        // Call the activation endpoint
        await AuthService.activateAccount(userId, activationId);
        setActivationSuccess(true);
        setError("");
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (err) {
        setActivationSuccess(false);
        // Handle different error types
        if (err.response && err.response.status === 404) {
          if (err.response.data && err.response.data.message) {
            if (err.response.data.message.includes("User not found")) {
              setError("User not found. Please check the activation link or contact support.");
            } else if (err.response.data.message.includes("Activation ID not found")) {
              setError("Activation link is invalid or expired. Please request a new activation link.");
            } else {
              setError("Activation link is invalid. Please check the link or contact support.");
            }
          } else {
            setError("Activation link is invalid. Please check the link or contact support.");
          }
        } else if (err.response && err.response.status === 400) {
          if (err.response.data && err.response.data.message) {
            if (err.response.data.message.includes("Invalid activation ID")) {
              setError("This activation link is not valid for your account. Please use the correct link or request a new one.");
            } else {
              setError("Invalid activation request. Please check the link or contact support.");
            }
          } else {
            setError("Invalid activation request. Please check the link or contact support.");
          }
        } else {
          setError("An unexpected error occurred during activation. Please try again or contact support.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (userId && activationId) {
      activateAccount();
    } else {
      setError("Invalid activation link. Please check the link or contact support.");
      setLoading(false);
    }
  }, [userId, activationId, navigate]);

  const handleRedirect = () => {
    navigate("/login");
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>
              {loading ? "hourglass_empty" : activationSuccess ? "check_circle" : "error"}
            </Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            {loading ? "Activating Account" : activationSuccess ? "Activation Successful" : "Activation Failed"}
          </MDTypography>
        </MDBox>

        {/* Message Content */}
        <MDBox textAlign="center" mb={3}>
          {loading && (
            <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
              Please wait while we activate your account...
            </MDTypography>
          )}

          {activationSuccess && (
            <>
              <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
                Your account has been successfully activated!
              </MDTypography>
              <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
                You can now log in to your account.
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ fontStyle: "italic" }}>
                Redirecting to login page...
              </MDTypography>
            </>
          )}

          {error && (
            <>
              <MDTypography variant="body1" color="text" sx={{ mb: 2 }}>
                {error}
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
                Please try again or contact support if the problem persists.
              </MDTypography>
            </>
          )}
        </MDBox>

        {/* Action Button */}
        {!loading && (
          <MDButton
            variant="gradient"
            color={activationSuccess ? "success" : "info"}
            fullWidth
            size="large"
            onClick={handleRedirect}
            sx={{
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            {activationSuccess ? "Go to Login" : "Back to Login"}
          </MDButton>
        )}
      </Card>
    </CustomBasicLayout>
  );
}

export default ActivateAccount;